/* Utility service for handling translations in frontend UI */
/* This service simplfifies access to the react-intl-universal module */
/* by automating defaultMessage and converting original english match strings */
/* to uppercase w underscore (eg: t('Send message') --> 'SEND_MESSAGE' in locale file) */

/* Docs: https://github.com/alibaba/react-intl-universal */

import intl from 'react-intl-universal';
import SessionStore from 'stores/shared/session';
import * as enUS from 'views/plugins/tours/locales/en-US.json';
import * as esES from 'views/plugins/tours/locales/es-ES.json';
import * as svSE from 'views/plugins/tours/locales/sv-SE.json';
import * as jaJP from 'views/plugins/tours/locales/ja-JP.json';
import * as frCA from 'views/plugins/tours/locales/fr-CA.json';
import * as frFR from 'views/plugins/tours/locales/fr-FR.json';
import * as deDE from 'views/plugins/tours/locales/de-DE.json';
import * as nlNL from 'views/plugins/tours/locales/nl-NL.json';
import * as zhCN from 'views/plugins/tours/locales/zh-CN.json';
import * as elGR from 'views/plugins/tours/locales/el-GR.json';
import * as itIT from 'views/plugins/tours/locales/it-IT.json';
import * as zhTW from 'views/plugins/tours/locales/zh-TW.json';

/* Load locale lookup */
const locales = {
  'en-US': enUS,
  'es-ES': esES,
  'sv-SE': svSE,
  'ja-JP': jaJP,
  'fr-CA': frCA,
  'fr-FR': frFR,
  'de-DE': deDE,
  'nl-NL': nlNL,
  'zh-CN': zhCN,
  'el-GR': elGR,
  'it-IT': itIT,
  'zh-TW': zhTW,
};

let basicSetloaded = false;

// TO-DO: Need to change this logic for capitalize, lowercase and uppercase. Not valid in everycase
const isCapitalized = (string) =>
  string[0].toUpperCase() === string[0] &&
  string[1].toLowerCase() === string[1];
const isUpperCase = (string) =>
  string[0].toUpperCase() === string[0] &&
  string[1].toUpperCase() === string[1];
const isLowerCase = (string) => string[0].toLowerCase() === string[0];

const capitalize = (string, firstOnly = true) =>
  string.charAt(0).toUpperCase() +
  (firstOnly ? string.slice(1) : string.slice(1).toLowerCase());

export const format = (key, translation, needFormatting) => {
  if (!translation || key.length < 2 || typeof translation !== 'string') {
    return translation;
  }

  if (needFormatting) {
    if (isCapitalized(key)) {
      return capitalize(translation);
    }
    if (isLowerCase(key)) {
      return translation.toLowerCase();
    }
    if (isUpperCase(key)) {
      return translation;
    }
  }

  return translation;
};

/* Call from topmost, earliest loaded component */
export const loadLocales = (locale, fallbackLocale = 'en-US') => {
  // If we already have the correct locale loaded, don't re-initialize
  if (basicSetloaded && intl.options.currentLocale === locale) {
    return;
  }

  // If we're trying to load a different locale than what we have, prevent override
  if (
    basicSetloaded &&
    intl.options.currentLocale &&
    intl.options.currentLocale !== locale
  ) {
    return;
  }

  // Do nothing in warningHander to prevent noisy console logging done by default.
  // Can be used in future to wire into 3rd party translation service to
  // log missing translations.
  const warningHandler = (message, detail) => {};

  // Init method will load CLDR locale data according to currentLocale
  // Note: react-intl-universal is a singleton, so you only init once.
  intl.init({
    currentLocale: locale || 'en-US',
    escapeHtml: false,
    locales,
    warningHandler,
    fallbackLocale,
  });
  basicSetloaded = true;
};

/* Usage t('Something to translate') -> looks up in locale file 'SOMETHING_TO_TRANSLATE' */
/* If no translation is found, default is the original 'Something to translate' */
export const keyForString = (stringToTranslate) =>
  stringToTranslate
    .replace(/\&/, 'and')
    .replace(/\W/g, ' ')
    .replace(/\s{2,}/g, ' ') // remove punctuation & special chars
    .trim()
    .split(' ')
    .join('_')
    .toUpperCase();

export const findLocale = () => {
  let locale = new URL(window.location.href).searchParams.get('lang');
  if (!locale && SessionStore.state) {
    locale = SessionStore.state.get('i18n'); // support old flux store
  }
  return locales[locale] ? locale : 'en-US';
};

export const t = (
  stringToTranslate,
  defaultString = false,
  options = { num: 1 },
  needFormatting = true
) => {
  if (!stringToTranslate) {
    return null;
  }
  defaultString = defaultString || stringToTranslate;

  if (!intl.options.currentLocale) {
    const locale = findLocale();
    loadLocales(locale);
  }

  // If custom translations are defined, t() will throw an error because the "key"
  // is already a translated string.
  let message;
  try {
    message = intl
      .get(keyForString(stringToTranslate), options)
      .defaultMessage(defaultString);
  } catch (error) {
    // Fallback to the default string
    message = defaultString;
  }

  return format(stringToTranslate, message, needFormatting);
};

export const tHtml = (
  stringToTranslate,
  defaultString = false,
  options = { num: 1 }
) => {
  if (!stringToTranslate) {
    return null;
  }
  defaultString = defaultString || stringToTranslate;
  if (!intl.options.currentLocale) {
    loadLocales(findLocale());
  }
  return format(
    stringToTranslate,
    intl
      .getHTML(keyForString(stringToTranslate), options)
      .defaultMessage(defaultString)
  );
};

// Given an object with additional translations in format:
// {
//   'en-US': {
//     key1: 'text',
//     key2: 'some text',
//   },
//   'es-ES': {
//     key1: 'text',
//     key2: 'some other text',
//   },
//   ...
// }
// will merge this into known translations and return a new translation function "t"
export const translateWithObject = (dictionary, locale = null) => {
  if (!basicSetloaded) {
    loadLocales(locale || findLocale(), 'en-US');
  }
  if (dictionary) {
    intl.load(dictionary);
  }
  return t;
};
